// Poznámky u kontaktu v Sales (1. 10. 2026, zadání Lády): fungují stejně jako komentáře
// u klientky a kandidáta. Víc poznámek, připnutí, úprava, archiv, smazání (jen owner).
// Převzato z KomentareKandidata (src/pages/hr/Kandidati.jsx), jen API vede do /api/sales.
import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, Loader2, MessageCircle, Pin, Plus, Archive } from "lucide-react";
import { CommentBubble, formatCommentTime } from "../Komentare.jsx";
import ConfirmDialog from "../ConfirmDialog.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { salesPost } from "./salesApi.js";

// Owner (i při náhledu za jinou roli si drží _realRole) smí mazat, ostatní archivují.
function jeOwner() {
  const u = getCurrentUser();
  return String((u && (u._realRole || u.role)) || "").toLowerCase() === "owner";
}

// post: jiné API (B2B posílá své), výchozí je sales. Ostatní volající zůstávají beze změny.
export default function KomentareKontaktu({ leadId, initial, onChanged, post = salesPost }) {
  const [comments, setComments] = useState(initial || []);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { action: "pin"|"archive"|"delete", comment, newValue? }
  const currentUser = getCurrentUser();
  const isOwner = jeOwner();
  const canDelete = isOwner;

  // Po každém načtení detailu se seznam srovná se serverem.
  useEffect(() => { setComments(initial || []); }, [initial]);

  const editComment = async (comment, newText) => {
    const snapshot = comments;
    const now = new Date().toISOString().substring(0, 19).replace("T", " ");
    setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, text: newText, aktualizovano: now } : c)));
    try {
      await post("komentar_upravit", { komentar_id: comment.id, text: newText });
      setError(null);
    } catch (e) {
      setComments(snapshot);
      setError(`Úprava poznámky selhala: ${e.message}`);
      throw e;
    }
  };

  const submitDraft = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setError(null);
    const tmpId = `tmp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const now = new Date().toISOString().substring(0, 19).replace("T", " ");
    const optimistic = {
      id: tmpId,
      text,
      autor_email: currentUser?.email || "",
      autor_name: currentUser?.name || currentUser?.email || "?",
      vytvoreno: now,
      pinned: false,
      archived: false,
      aktualizovano: now,
      edit_history: [],
      _optimistic: true,
    };
    const draftSnapshot = draft;
    setComments((prev) => [...prev, optimistic]);
    setDraft("");
    setPosting(true);
    try {
      const j = await post("komentar", { id: leadId, text });
      setComments((prev) => prev.map((c) => (c.id === tmpId ? j.komentar : c)));
      onChanged?.();
    } catch (e) {
      setComments((prev) => prev.filter((c) => c.id !== tmpId));
      setDraft(draftSnapshot);
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  const askPin = (comment, newValue) => setConfirmDialog({ action: "pin", comment, newValue });
  const askArchive = (comment, newValue) => setConfirmDialog({ action: "archive", comment, newValue });
  const askDelete = (comment) => setConfirmDialog({ action: "delete", comment });
  const confirmAction = async () => {
    if (!confirmDialog) return;
    const { action, comment, newValue } = confirmDialog;
    setConfirmDialog(null);
    const snapshot = comments;
    if (action === "delete") {
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
    } else {
      setComments((prev) => prev.map((c) => (c.id === comment.id ? { ...c, [action === "pin" ? "pinned" : "archived"]: newValue } : c)));
    }
    try {
      if (action === "delete") await post("komentar_smazat", { komentar_id: comment.id });
      else await post("komentar_upravit", { komentar_id: comment.id, [action === "pin" ? "pinned" : "archived"]: newValue });
      setError(null);
      onChanged?.();
    } catch (e) {
      setComments(snapshot);
      setError(`Akce "${action}" selhala: ${e.message}`);
    }
  };

  const pinned = comments.filter((c) => c.pinned && !c.archived);
  const active = comments.filter((c) => !c.pinned && !c.archived).sort((a, b) => (a.vytvoreno || "").localeCompare(b.vytvoreno || ""));
  const archived = comments.filter((c) => c.archived).sort((a, b) => (b.vytvoreno || "").localeCompare(a.vytvoreno || ""));
  const totalActive = pinned.length + active.length;
  const bublina = (c) => (
    <CommentBubble
      key={c.id}
      comment={c}
      canEdit
      canDelete={canDelete}
      canEditThis={isOwner || c.autor_email === currentUser?.email}
      onPin={askPin}
      onArchive={askArchive}
      onDelete={askDelete}
      onEdit={editComment}
    />
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <ConfirmDialog
        open={!!confirmDialog}
        title={
          confirmDialog?.action === "pin"
            ? (confirmDialog?.newValue ? "Připnout poznámku?" : "Odepnout poznámku?")
            : confirmDialog?.action === "archive"
              ? (confirmDialog?.newValue ? "Skrýt do archivu?" : "Obnovit z archivu?")
              : "Trvale smazat poznámku?"
        }
        description={
          confirmDialog ? (
            <div className="space-y-2 mt-2 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 italic text-slate-700">
                "{confirmDialog.comment.text.substring(0, 120)}{confirmDialog.comment.text.length > 120 ? "…" : ""}"
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-medium">{confirmDialog.comment.autor_name || confirmDialog.comment.autor_email}</span>
                {confirmDialog.comment.vytvoreno && `, ${formatCommentTime(confirmDialog.comment.vytvoreno)}`}
              </div>
              {confirmDialog.action === "pin" && (
                <div className="text-slate-500 text-xs">
                  {confirmDialog.newValue
                    ? "Poznámka se zobrazí nahoře jako důležitá."
                    : "Poznámka se vrátí mezi ostatní."}
                </div>
              )}
              {confirmDialog.action === "archive" && (
                <div className="text-slate-500 text-xs">
                  {confirmDialog.newValue
                    ? "Poznámka se skryje do archivu, ale zůstane v historii a jde obnovit."
                    : "Poznámka se vrátí mezi ostatní."}
                </div>
              )}
              {confirmDialog.action === "delete" && (
                <div className="rounded-xl border border-rose-300 bg-rose-50 p-2.5 text-xs text-rose-800 leading-relaxed">
                  <div className="font-bold mb-0.5 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Trvalé smazání
                  </div>
                  <div>
                    Poznámka bude <strong>trvale odstraněna</strong>. Tato akce <strong>nelze vrátit zpět</strong>.
                    Pokud chceš jen schovat, zruš a zvol Archiv místo toho.
                  </div>
                </div>
              )}
            </div>
          ) : ""
        }
        confirmLabel={
          confirmDialog?.action === "pin"
            ? (confirmDialog?.newValue ? "Připnout" : "Odepnout")
            : confirmDialog?.action === "archive"
              ? (confirmDialog?.newValue ? "Skrýt do archivu" : "Obnovit")
              : "Trvale smazat"
        }
        cancelLabel="Zrušit"
        variant={
          confirmDialog?.action === "delete"
            ? "danger"
            : (confirmDialog?.action === "archive" && confirmDialog?.newValue ? "warning" : "default")
        }
        onConfirm={confirmAction}
        onCancel={() => setConfirmDialog(null)}
      />

      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-brand-500" />
          <span className="text-sm font-bold text-slate-800">Poznámky</span>
          {totalActive > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-brand-50 text-brand-700 border border-brand-200 tabular-nums">{totalActive}</span>
          )}
          {pinned.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200 tabular-nums">
              <Pin className="w-3 h-3" /> {pinned.length}
            </span>
          )}
        </div>
      </div>

      {/* Vstup: Enter odešle, Shift+Enter nový řádek. */}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              submitDraft();
            }
          }}
          placeholder="Napiš poznámku, Enter uloží"
          rows={1}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
          }}
          disabled={posting}
          data-gramm="false"
          className="field min-w-0 flex-1 resize-none disabled:opacity-50"
          style={{ maxHeight: 150 }}
        />
        <button
          type="button"
          onClick={submitDraft}
          disabled={!draft.trim() || posting}
          className="btn btn-sm btn-primary shrink-0"
          title="Odeslat (Enter)"
        >
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat
        </button>
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {totalActive === 0 && archived.length === 0 && (
        <div className="mt-3 text-xs text-slate-400">Zatím žádné poznámky.</div>
      )}

      <div className="mt-3 space-y-2.5">
        {pinned.length > 0 && <div className="space-y-2.5">{pinned.map(bublina)}</div>}
        {pinned.length > 0 && active.length > 0 && (
          <div className="flex items-center gap-2 my-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Ostatní</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        )}
        {active.map(bublina)}
        {archived.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setShowArchive((v) => !v)}
              aria-expanded={showArchive}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 min-h-[44px] sm:min-h-0"
            >
              <Archive className="h-4 w-4 shrink-0 text-slate-400" />
              <span>Archiv ({archived.length})</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${showArchive ? "rotate-180" : ""}`} />
            </button>
            {showArchive && <div className="space-y-2.5 mt-3">{archived.map(bublina)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
